import React from 'react';
import { ICoreContext } from '@msdyn365-commerce/core';
import { ArrayExtensions, createInventoryAvailabilitySearchCriteria, DeliveryMode, IProductInventoryInformation, mapAggregatedProductInventoryInformation } from '@msdyn365-commerce-modules/retail-actions';
import { ProductListProduct } from '../product-list-component/product-list-component';
import { ProductSearchResultExt } from '../../shared-utils/types';
import { ProductsDataActions, SimpleProduct, UnitOfMeasureConversion } from '@msdyn365-commerce/retail-proxy';
import { applyAttributesToProduct } from '../../shared-utils/attributes';
import { getProductUnitOfMeasureOptionsAsync } from '../../actions/get-product-unit-of-measure-options.action';
import { BrandSchemesWhitelist, getBrandSchemesByIdsAsync } from '../../actions/get-brand-schemes.action';
import { IBrandSchemeEntity } from '../../actions/entities/brand-scheme-entity';
import { IProductRelationTypeForProduct } from '../../actions/get-relation-types-by-ids';
import { AlternativeProductLine } from './alternative-product-line';
import { arrayUniqueByProperty } from '../../shared-utils/common-utils';

export interface IAlternativeProductsResources {
    alternativeProductsLoadingText?: string;
    alternativeProductsErrorText?: string;
    alternativeProductsNoResultsText?: string;
    alternativeProductsChangeProductButtonText?: string;
}

export type OnChangeProductFunction = (
    product: ProductListProduct,
    replacement: ProductListProduct,
    replacementSimpleProduct: SimpleProduct | undefined,
    unitConversions: UnitOfMeasureConversion[] | undefined,
    recommendationType: string,
    position: number,
    quantity: number,
    breakoff: boolean
) => Promise<boolean>;

type AlternativeProductsProps = {
    id: string;
    context: ICoreContext;
    typeName: string;
    mode: 'edit' | 'view';
    resources: IAlternativeProductsResources;
    product: ProductListProduct;
    relationTypesByIds?: IProductRelationTypeForProduct[];
    onChangeProduct?: OnChangeProductFunction;
    close?: () => void;
};

function getSorterByAvailability(availability: IProductInventoryInformation[] | undefined) {
    return (a: ProductSearchResultExt, b: ProductSearchResultExt): number => {
        const aAvailability = availability?.find(avail => avail.ProductAvailableQuantity.ProductId === a.RecordId);
        const bAvailability = availability?.find(avail => avail.ProductAvailableQuantity.ProductId === b.RecordId);
        const aAvailable = aAvailability ? (aAvailability.ProductAvailableQuantity.AvailableQuantity || 0) > 0 : false;
        const bAvailable = bAvailability ? (bAvailability.ProductAvailableQuantity.AvailableQuantity || 0) > 0 : false;
        if (aAvailable && !bAvailable) {
            return -1;
        }
        if (!aAvailable && bAvailable) {
            return 1;
        }
        return 0;
    };
}

export function AlternativeProducts({
    id,
    context,
    typeName,
    mode,
    resources,
    product,
    relationTypesByIds,
    onChangeProduct
}: AlternativeProductsProps) {
    const [state, setState] = React.useState<'loading' | 'error' | 'no-results' | 'done'>('loading');
    const [products, setProducts] = React.useState<ProductSearchResultExt[]>([]);
    const [simpleProducts, setSimpleProducts] = React.useState<SimpleProduct[]>([]);
    const [unitOfMeasureConversions, setUnitOfMeasureConversions] = React.useState<UnitOfMeasureConversion[]>([]);
    const [_brandSchemes, setBrandSchemes] = React.useState<IBrandSchemeEntity[]>([]);
    const [estimatedAvailabilities, setEstimatedAvailabilities] = React.useState<IProductInventoryInformation[]>([]);

    React.useEffect(() => {
        setState('loading');

        void (async () => {
            const { channelId, catalogId } = context.request.apiSettings;
            const relationTypes = relationTypesByIds?.filter(x => x.ProductId === product.RecordId);

            if (!relationTypes || relationTypes.length === 0) {
                setState('no-results');
                return;
            }

            const promises = relationTypes.map(async relationType => {
                // Attach relationType to results for later use
                const results = await ProductsDataActions.getRelatedProductsAsync(
                    { callerContext: context.actionContext },
                    product.RecordId,
                    channelId,
                    catalogId,
                    relationType.RecordId!
                );
                results.forEach(p => {
                    (p as ProductSearchResultExt).RelationTypes = [relationType];
                });
                return results;
            });

            const allRelatedProducts = await Promise.all(promises);

            // Assumingly the same product can be related via different relation types, so flatten and deduplicate.
            // Take RelationTypes from all occurrences and merge into one.
            const seenRecIds = new Set<number>();
            const dedupedProducts: ProductSearchResultExt[] = [];

            allRelatedProducts.forEach((p) => {
                p.forEach((prod) => {
                    if (seenRecIds.has(prod.RecordId)) {
                        // Already seen, merge RelationTypes by name
                        const existingProd = dedupedProducts.find(x => x.RecordId === prod.RecordId);
                        if (existingProd && (prod as ProductSearchResultExt).RelationTypes) {
                            existingProd.RelationTypes = arrayUniqueByProperty([
                                ...(existingProd.RelationTypes || []),
                                ...(prod as ProductSearchResultExt).RelationTypes!
                            ], 'Name');
                        }
                    } else {
                        seenRecIds.add(prod.RecordId);
                        dedupedProducts.push(prod);
                    }
                });
            });

            if (dedupedProducts.length === 0) {
                setState('no-results');
                return;
            }

            // Sort by DisplayOrder ascending
            dedupedProducts.sort((a, b) => (a.DisplayOrder || 0) - (b.DisplayOrder || 0));

            dedupedProducts.forEach((p) => {
                applyAttributesToProduct(context.request, p, p.AttributeValues, undefined, false);
            });

            setProducts(dedupedProducts);

            const productIds = ArrayExtensions.unique(dedupedProducts.map(p => p.RecordId));
            const pricesPromise = ProductsDataActions.getActivePricesAsync(
                { callerContext: context.actionContext },
                {
                    CatalogId: catalogId,
                    ChannelId: channelId
                },
                productIds,
                new Date(),
                null,
                undefined,
                true
            );
            const simpleProductsPromise = ProductsDataActions.getByIdsAsync(
                { callerContext: context.actionContext },
                channelId,
                productIds,
                null,
                catalogId
            );
            const unitOfMeasureConversionsPromise = getProductUnitOfMeasureOptionsAsync({ callerContext: context.actionContext }, productIds);
            const brandSchemesIds = ArrayExtensions.unique(dedupedProducts.flatMap(x => x.SpecialBadges?.split('|')).filter(Boolean) || []).filter(id => BrandSchemesWhitelist.includes(id!)) as string[];
            const brandSchemesPromise = brandSchemesIds.length === 0 ? Promise.resolve([]) : getBrandSchemesByIdsAsync({ callerContext: context.actionContext }, brandSchemesIds);
            const estimatedAvailabilitiesPromise = ProductsDataActions.getEstimatedAvailabilityAsync(
                { callerContext: context.actionContext },
                createInventoryAvailabilitySearchCriteria(context.actionContext, productIds, true, undefined, undefined, DeliveryMode.shipping)
            );
            const categoryPathsPromise = ProductsDataActions.getCategoryPathsAsync(
                { callerContext: context.actionContext },
                channelId,
                catalogId,
                productIds.map(p => ({ ProductId: p }))
            );

            const [
                prices,
                simpleProducts,
                unitOfMeasureConversions,
                brandSchemes,
                estimatedAvailabilities,
                categoryPaths
            ] = await Promise.all([pricesPromise, simpleProductsPromise, unitOfMeasureConversionsPromise, brandSchemesPromise, estimatedAvailabilitiesPromise, categoryPathsPromise]);

            dedupedProducts.forEach((p) => {
                const categoryPath = categoryPaths?.find(cp => cp.ProductId === p.RecordId)?.CategoryPath;
                applyAttributesToProduct(context.request, p, undefined, categoryPath, true);
                p.ProductPrice = prices.find(price => price.ProductId === p.RecordId);
            });

            setUnitOfMeasureConversions(unitOfMeasureConversions);
            setBrandSchemes(brandSchemes);
            setProducts(dedupedProducts);
            setSimpleProducts(simpleProducts);

            if (estimatedAvailabilities.ProductWarehouseInventoryAvailabilities) {
                const shippingInventory = mapAggregatedProductInventoryInformation(context.actionContext, estimatedAvailabilities);
                if (ArrayExtensions.hasElements(shippingInventory)) {
                    shippingInventory.forEach((x) => {
                        x.deliveryType = DeliveryMode.shipping;
                        x.ProductAvailableQuantity.ExtensionProperties = estimatedAvailabilities.ProductWarehouseInventoryAvailabilities
                            ?.find(y => y.ProductId === x.ProductAvailableQuantity.ProductId)?.ExtensionProperties;
                    });
                    setEstimatedAvailabilities(shippingInventory);
                }
            }

            setState('done');
        })();
    }, [product.RecordId]);

    const sortByAvailability = React.useMemo(() => getSorterByAvailability(estimatedAvailabilities), [estimatedAvailabilities]);

    const baseClassName = 'dlvry-alternative-products-popup';

    return (
        <div className={baseClassName}>
            {state === 'loading' && (
                <div className={`${baseClassName}__loading`}>{resources.alternativeProductsLoadingText}</div>
            )}
            {state === 'error' && (
                <div className={`${baseClassName}__error`}>{resources.alternativeProductsErrorText}</div>
            )}
            {state === 'no-results' && (
                <div className={`${baseClassName}__no-results`}>{resources.alternativeProductsNoResultsText}</div>
            )}
            {state === 'done' && (
                <ul className={`${baseClassName}__list`}>
                    {products?.slice(0, 3).sort(sortByAvailability).map((p, i) => (
                        <AlternativeProductLine
                            id={id}
                            typeName={typeName}
                            key={p.RecordId}
                            context={context}
                            mode={mode}
                            resources={resources}
                            product={{
                                ...p,
                                BreakoffProductKey: product.RecordId.toString()
                            }}
                            simpleProduct={simpleProducts.find(sp => sp.RecordId === p.RecordId)}
                            unitConversions={unitOfMeasureConversions}
                            availability={estimatedAvailabilities.find(ea => ea.ProductAvailableQuantity.ProductId === p.RecordId)}
                            onChangeProduct={async (replacement, quantity, breakoff) => {
                                if (!onChangeProduct) {
                                    return true;
                                }

                                // Copy UOM from simple product to replacement product
                                const replacementSimpleProduct = simpleProducts.find(sp => sp.RecordId === replacement.RecordId);
                                if (replacementSimpleProduct) {
                                    replacement.DefaultUnitOfMeasure = replacementSimpleProduct.DefaultUnitOfMeasure;
                                }

                                var result = await onChangeProduct?.(
                                    product,
                                    replacement,
                                    replacementSimpleProduct,
                                    unitOfMeasureConversions,
                                    p.RelationTypes?.map(x => x.Name).filter(Boolean).join(', ') || 'Unknown',
                                    i,
                                    quantity,
                                    breakoff
                                );

                                return result;
                            }}
                        />
                    ))}
                </ul>
            )}
        </div>
    );
}
